import Decimal from 'decimal.js';
import { useCallback, useEffect, useState } from 'react';
import { Input, type InputProps } from './Input';

export interface DecimalInputProps extends Omit<InputProps, 'value' | 'onChange' | 'type'> {
  value: Decimal | null;
  onChange: (value: Decimal | null) => void;
  onlyPositive?: boolean;
}

function normalizeRawDecimalInput(raw: string): string {
  if (raw === '') return '';

  const hasLeadingMinus = raw.startsWith('-');
  const sign = hasLeadingMinus ? '-' : '';
  const unsigned = raw.replace(/-/g, '');
  const [integerPart = '', fractionalPart] = unsigned.split(/[.,]/, 2);
  const separatorMatch = unsigned.match(/[.,]/);
  const separator = separatorMatch?.[0];

  const normalizedInteger = integerPart === '' ? '' : integerPart.replace(/^0+(?=\d)/, '') || '0';

  if (!separator) return `${sign}${normalizedInteger}`;

  const integerWithSeparator = normalizedInteger === '' ? '0' : normalizedInteger;

  // Every amount and percentage column is Decimal(10, 2): a third decimal would be rounded away on save.
  return `${sign}${integerWithSeparator}${separator}${(fractionalPart ?? '').slice(0, 2)}`;
}

const toRaw = (value: Decimal): string => value.toFixed().replace('.', ',');

export default function DecimalInput({
  value,
  onChange,
  onlyPositive = false,
  ...rest
}: DecimalInputProps) {
  const [raw, setRaw] = useState<string>(value ? toRaw(value) : '');

  const parseDecimal = useCallback(
    (s: string): Decimal | null => {
      if (s.trim() === '') return null;
      try {
        const normalized = s.replace(',', '.');
        const d = new Decimal(normalized);
        return onlyPositive ? d.abs() : d;
      } catch {
        return null;
      }
    },
    [onlyPositive]
  );

  useEffect(() => {
    if (value === null) {
      if (raw.trim() !== '' && parseDecimal(raw) !== null) setRaw('');
    } else {
      if (parseDecimal(raw)?.eq(value) !== true) setRaw(toRaw(value));
    }
  }, [parseDecimal, raw, value]);

  return (
    <Input
      type="text"
      inputMode="decimal"
      value={raw}
      onChange={(e) => {
        const sanitized = e.target.value.replace(/[^0-9.,-]/g, '');
        const normalizedInput = onlyPositive ? sanitized.replace(/-/g, '') : sanitized;
        const newRaw = normalizeRawDecimalInput(normalizedInput);
        setRaw(newRaw);
        const d = parseDecimal(newRaw);
        const valuesEqual = d === null ? value === null : value !== null && d.eq(value);
        if (!valuesEqual) onChange(d);
      }}
      {...rest}
    />
  );
}
